import { createTRPCProxyClient, httpBatchLink } from "@trpc/client";
import type { AppRouter } from "@app/server/trpc/router";
import type { PrintJobStatusReport } from "@app/server/trpc/routers/print-jobs";
import { routingFetch } from "../helpers";
import { nanoid } from "nanoid";
import { lemmonEnv, release } from "../../version";

// Best-effort reporting of print job outcomes to print_job_statuses.
//
// Nothing here may affect printing: every entry point returns synchronously,
// swallows its own errors, and talks to the API in the background. Reports are
// kept in localStorage until the server confirms them, so a reload or a network
// blip delays a report instead of losing it.
//
// The queue outlives logout and is shared by every account on the device, so
// each report carries its restaurantId and is only ever sent while that
// restaurant is the one logged in (the server checks it again). Another
// restaurant's leftovers wait for it, or expire after a day.

const QUEUE_KEY = "lemmon.printJobStatusQueue";
const DEVICE_ID_KEY = "lemmon.deviceId";
const MAX_QUEUE_SIZE = 500;
const MAX_REPORT_AGE_MS = 24 * 60 * 60 * 1000;
const BATCH_SIZE = 100;
const FLUSH_DELAY_MS = 1000;
const RETRY_DELAY_MS = 30 * 1000;
// A flush still running after this long is treated as lost (e.g. the request
// was cut off while the device was locked), so later flushes aren't blocked.
// Re-sending is harmless: the server ignores repeated reports.
const STALE_FLUSH_MS = 30 * 1000;

type QueuedReport = { report: PrintJobStatusReport; queuedAt: number };
type Queue = Record<string, QueuedReport>;

const STATUS_RANK: Record<PrintJobStatusReport["status"], number> = {
  sending: 1,
  failed: 2,
  printed: 3,
};

// A job's reports can arrive out of order; keep the most advanced status and
// carry over fields the newer report doesn't have.
export const mergeReport = (
  existing: PrintJobStatusReport | undefined,
  next: PrintJobStatusReport,
): PrintJobStatusReport => {
  if (!existing) return next;
  if (STATUS_RANK[next.status] < STATUS_RANK[existing.status]) return existing;
  return { ...existing, ...stripUndefined(next) };
};

const stripUndefined = <T extends object>(obj: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined),
  ) as Partial<T>;

// The ePOS-Print service answers HTTP 200 with the printer's verdict in the
// body, e.g. <response success="false" code="EPTR_REC_EMPTY" .../>.
export const parseEposResponse = (
  body: string | null | undefined,
): { success: boolean | null; code: string | null } => {
  if (!body) return { success: null, code: null };
  const tag = body.match(/<response\b[^>]*>/i)?.[0];
  if (!tag) return { success: null, code: null };
  const success = tag.match(/\bsuccess="([^"]*)"/i)?.[1];
  const code = tag.match(/\bcode="([^"]*)"/i)?.[1];
  return {
    success:
      success === undefined ? null : success === "true" || success === "1",
    code: code ? code : null,
  };
};

/** Up to `size` queued reports belonging to `restaurantId`. */
export const selectBatch = (
  queue: Queue,
  restaurantId: number,
  size = BATCH_SIZE,
) =>
  Object.values(queue)
    .filter((entry) => entry.report.restaurantId === restaurantId)
    .slice(0, size)
    .map((entry) => entry.report);

export const pruneQueue = (queue: Queue, now: number): Queue => {
  const fresh = Object.entries(queue)
    .filter(([, entry]) => now - entry.queuedAt < MAX_REPORT_AGE_MS)
    .sort(([, a], [, b]) => b.queuedAt - a.queuedAt)
    .slice(0, MAX_QUEUE_SIZE);
  return Object.fromEntries(fresh);
};

const readQueue = (): Queue => {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? (JSON.parse(raw) as Queue) : {};
  } catch {
    return {};
  }
};

const writeQueue = (queue: Queue) => {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // storage full or unavailable: reports are best-effort
  }
};

export const getReportingDeviceId = (): string | null => {
  try {
    // Prefer PostHog's device id so rows can be matched against analytics.
    const posthogId = (window as any).posthog?.get_property?.("$device_id");
    if (typeof posthogId === "string" && posthogId) return posthogId;
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = nanoid();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

// What this device runs, stored with each report (print_job_statuses.client_info):
// the web build plus what the native app injects as window.lemmonEnv.
export const getReportingClientInfo = (): Record<
  string,
  string | number | boolean | null
> => {
  try {
    const device = lemmonEnv?.device ?? {};
    const app = lemmonEnv?.app ?? {};
    const info: Record<string, string | number | boolean | null> = {
      release,
      model: device.model ?? null,
      os: device.os ?? null,
      osVersion: device.osVersion ?? null,
      appVersion: app.version ?? null,
      appBuild: app.build ?? null,
      otaUpdateId: app.ota?.updateId ?? null,
    };
    // Keep only scalars short enough for the server's limits.
    for (const [key, value] of Object.entries(info)) {
      if (typeof value === "string") info[key] = value.slice(0, 200);
      else if (
        value != null &&
        typeof value !== "number" &&
        typeof value !== "boolean"
      )
        info[key] = null;
    }
    return info;
  } catch {
    return { release };
  }
};

let client: ReturnType<typeof createTRPCProxyClient<AppRouter>> | null = null;
const getClient = () => {
  if (!client) {
    client = createTRPCProxyClient<AppRouter>({
      links: [httpBatchLink({ url: "/api/trpc", fetch: routingFetch })],
    });
  }
  return client;
};

let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushStartedAt: number | null = null;
// The restaurant this device is printing for right now; set by each report.
let currentRestaurantId: number | null = null;

const scheduleFlush = (delayMs: number) => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushPrintJobStatuses();
  }, delayMs);
};

export const flushPrintJobStatuses = async () => {
  if (flushStartedAt != null && Date.now() - flushStartedAt < STALE_FLUSH_MS)
    return;
  const startedAt = Date.now();
  flushStartedAt = startedAt;
  try {
    if (currentRestaurantId == null) return;
    const queue = pruneQueue(readQueue(), Date.now());
    const batch = selectBatch(queue, currentRestaurantId);
    if (batch.length === 0) return;

    let accepted: string[];
    try {
      accepted = (await getClient().printJobs.report.mutate({ reports: batch }))
        .accepted;
    } catch (e: any) {
      // A batch the server rejects as invalid will never succeed; drop it
      // instead of retrying it forever. Anything else is retried later.
      if (e?.data?.code === "BAD_REQUEST") {
        accepted = batch.map((r) => r.jobId);
      } else {
        scheduleFlush(RETRY_DELAY_MS);
        return;
      }
    }

    // Re-read: reports may have been queued while the request was in flight.
    const latest = readQueue();
    for (const report of batch) {
      const current = latest[report.jobId];
      // Only drop what was sent; a newer status for the same job stays queued.
      if (
        accepted.includes(report.jobId) &&
        current &&
        current.report.status === report.status
      ) {
        delete latest[report.jobId];
      }
    }
    writeQueue(latest);
    if (selectBatch(latest, currentRestaurantId, 1).length > 0)
      scheduleFlush(FLUSH_DELAY_MS);
  } catch {
    scheduleFlush(RETRY_DELAY_MS);
  } finally {
    // A stale flush finishing late mustn't clear a newer one's marker.
    if (flushStartedAt === startedAt) flushStartedAt = null;
  }
};

// Phones and tablets get locked or put in the background right after sending
// an order, which pauses timers: a result that came in just before that would
// otherwise wait for the next print. Send what's queued as soon as the app is
// back on screen or back online. Registered on the first report, so nothing
// runs on devices that don't track.
let resumeListenersAdded = false;
const addResumeListeners = () => {
  if (resumeListenersAdded || typeof window === "undefined") return;
  resumeListenersAdded = true;
  const flushNow = () => {
    if (
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    )
      return;
    void flushPrintJobStatuses();
  };
  document.addEventListener("visibilitychange", flushNow);
  window.addEventListener("pageshow", flushNow);
  window.addEventListener("online", flushNow);
};

export const reportPrintJobStatus = (report: PrintJobStatusReport) => {
  try {
    currentRestaurantId = report.restaurantId;
    const queue = readQueue();
    queue[report.jobId] = {
      report: mergeReport(queue[report.jobId]?.report, report),
      queuedAt: queue[report.jobId]?.queuedAt ?? Date.now(),
    };
    writeQueue(pruneQueue(queue, Date.now()));
    scheduleFlush(FLUSH_DELAY_MS);
    addResumeListeners();
  } catch {
    // best-effort
  }
};

// Leftovers from before the last reload are sent with the next report, once
// it tells us which restaurant is logged in.
